import { forwardRef } from "react";

import { Icon } from "./icon";
import {
  passwordInputButtonVariants,
  passwordInputControlVariants,
  passwordInputRootVariants,
  type PasswordInputButtonVariants,
  type PasswordInputControlVariants,
  type PasswordInputRootVariants,
} from "./password-input.styles";
import { formatAriaInvalid } from "./style.utils";

// -----------------------------------------------------------------------------

export type PasswordInputRootRef = React.ComponentRef<"span">;

export interface PasswordInputRootProps
  extends React.ComponentPropsWithoutRef<"span">, PasswordInputRootVariants {}

const PasswordInputRoot = forwardRef<
  PasswordInputRootRef,
  PasswordInputRootProps
>(({ className, disabled, invalid, ...props }, ref) => {
  return (
    <span
      {...props}
      className={passwordInputRootVariants({ className, disabled, invalid })}
      ref={ref}
    />
  );
});

PasswordInputRoot.displayName = "PasswordInputRoot";

export { PasswordInputRoot };

// -----------------------------------------------------------------------------

export type PasswordInputControlRef = React.ComponentRef<"input">;

export interface PasswordInputControlProps
  extends
    Omit<React.ComponentPropsWithoutRef<"input">, "size">,
    Omit<PasswordInputControlVariants, "invalid"> {
  name: string;
  type?: "password" | "text";
}

const PasswordInputControl = forwardRef<
  PasswordInputControlRef,
  PasswordInputControlProps
>(
  (
    {
      "aria-invalid": ariaInvalid,
      className,
      disabled,
      size,
      type = "password",
      ...props
    },
    ref,
  ) => {
    return (
      <input
        {...props}
        type={type}
        disabled={disabled}
        aria-invalid={ariaInvalid}
        className={passwordInputControlVariants({
          className,
          disabled,
          invalid: formatAriaInvalid(ariaInvalid),
          size,
        })}
        ref={ref}
      />
    );
  },
);

PasswordInputControl.displayName = "PasswordInputControl";

export { PasswordInputControl };

// -----------------------------------------------------------------------------

export type PasswordInputButtonRef = React.ComponentRef<"button">;

export interface PasswordInputButtonProps
  extends
    Omit<React.ComponentPropsWithoutRef<"button">, "children" | "type">,
    PasswordInputButtonVariants {}

const PasswordInputButton = forwardRef<
  PasswordInputButtonRef,
  PasswordInputButtonProps
>(({ active, className, disabled, invalid, size, ...props }, ref) => {
  return (
    <button
      {...props}
      type="button"
      disabled={disabled}
      aria-pressed={active}
      className={passwordInputButtonVariants({
        className,
        disabled,
        invalid,
        active,
        size,
      })}
      ref={ref}
    >
      <Icon
        name={active ? "ms-visibility-off-filled" : "ms-visibility-filled"}
        size={size}
      />
      <span className="sr-only">{active ? "Hide" : "Show"}</span>
    </button>
  );
});

PasswordInputButton.displayName = "PasswordInputButton";

export { PasswordInputButton };
